/**
 * PBX Action/Target Validation Utilities
 *
 * Centralizes validation logic for PBX action/target dropdown pairs
 * used across Ring Groups, IVR, Extensions, and other components.
 *
 * Actions that require targets are defined in:
 * api/v1/endpoints/dropdowns.php line 227
 */

/**
 * Action IDs that require a target to be selected
 * Based on dropdowns.php requires_target check
 */
export const ACTIONS_REQUIRING_TARGET = [
  1,  // Ring Group
  3,  // Call Queue
  4,  // Extension
  7,  // Voicemail
  11, // Outside Number
  16, // IVR
  17, // Direct to Voicemail
  19, // Conference
  20, // Paging
  21, // Find Me Follow Me
];

/**
 * Action ID for "End Call" which doesn't require a target
 */
export const END_CALL_ACTION_ID = 9;

/**
 * Validates that an action/target pair is complete
 *
 * @param actionId - The selected action ID
 * @param target - The selected target value
 * @returns Object with isValid boolean and optional error message
 */
export function validateActionTarget(
  actionId: number | null | undefined,
  target: string | null | undefined
): { isValid: boolean; error?: string } {
  // No action selected or End Call selected - always valid
  if (!actionId || actionId === END_CALL_ACTION_ID) {
    return { isValid: true };
  }

  // Action requires target but none selected
  if (ACTIONS_REQUIRING_TARGET.includes(actionId)) {
    if (!target) {
      return {
        isValid: false,
        error: 'Please select a target for the selected action'
      };
    }
  }

  return { isValid: true };
}

/**
 * Checks if a specific action requires a target
 *
 * @param actionId - The action ID to check
 * @returns true if the action requires a target
 */
export function actionRequiresTarget(actionId: number | null | undefined): boolean {
  if (!actionId || actionId === END_CALL_ACTION_ID) {
    return false;
  }
  return ACTIONS_REQUIRING_TARGET.includes(actionId);
}
